<template>
  <div v-cus-loading="form.loading">
    <signup-form-with-pricing
      v-if="model.registerSuccess === false"
      ref="signupForm"
      :plan="plan"
      :original-price="price"
      :add-ons-mandatory="addOnsMandatory"
      :recurrence="recurrence"
      :coupon-data="coupon"
      :company="company"
      :service="service"
      :is-loading="isFormLoading"
      :company-identification-check="form.model.company.companyIdentificationCheck"
      @email-changed="onChangeEmail"
      @identification-changed="onChangeIdentification"
      @addon-changed="onAdditionalUsersChange"
      @register="onRegister"
    />
    <div v-if="model.registerSuccess === true">
  <div class="success-page">
    <!-- Confetti Animation - Solo se muestra cuando registerSuccess es true -->
    <div class="confetti-container">
      <div
        v-for="n in 50"
        :key="n"
        class="confetti-emoji"
        :style="getConfettiStyle(n)"
      >
        {{ getRandomEmoji() }}
      </div>
    </div>

    <div class="success-content">
      <!-- Success Icon -->
      <div class="success-icon">
        <div class="checkmark-circle">
          <svg class="checkmark" viewBox="0 0 48 48">
            <defs>
              <linearGradient id="checkmarkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style="stop-color:#4F46E5;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#7C3AED;stop-opacity:1" />
              </linearGradient>
            </defs>
            <circle class="checkmark-circle-bg" cx="24" cy="24" r="22" fill="none" stroke="url(#checkmarkGradient)" stroke-width="4" />
            <path class="checkmark-check" fill="none" stroke="url(#checkmarkGradient)" stroke-width="4" d="M14 22l8 8 12-12" />
          </svg>
        </div>
      </div>

      <!-- Success Message -->
      <div class="success-message">
        <h1 class="success-title">
¡Gracias por registrarte!
</h1>
        <p class="success-description">
          Estamos emocionados de tenerte como cliente. Te enviaremos un email con los datos de acceso a tu cuenta.
        </p>
      </div>

      <!-- CTA Button -->
      <div class="success-actions">
        <el-button
          type="primary"
          size="large"
          class="success-button"
          @click="onGoToService()"
        >
          Comenzar a utilizar mi cuenta
        </el-button>
      </div>
    </div>
  </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

// #region  app ...
import calculateIdentificationCheck from '@/helpers/identification-check'
import { validateForm } from '@/helpers/messenger'
import { i18n } from '@/i18n'
import create from '@/views/common/create'
import dialog, { showDialog } from '@/views/common/dialog'
import form from '@/views/common/form'
import model from './model'
import rules from './rules-user'
import roundPrecision from '@/helpers/round'

// Importar el nuevo componente
import SignupFormWithPricing from './signup-form-with-pricing.vue'

// #endregion

export default {
  components: {
    SignupFormWithPricing,
  },
  extends: create('companies', { model }),
  props: {
    company: { type: Object, required: true },
    service: { type: Object, required: true },
    plan: { type: Object, required: false, default: () => ({}) },
    coupon: { type: Object, required: false, default: () => ({}) },
    serviceTaxValue: { type: Number, required: false, default: 0 },
    serviceAmount: { type: Number, required: false, default: 0 },
    serviceAmountTotal: { type: Number, required: false, default: 0 },
    addOnsMandatory: { type: Array, required: false, default: () => [] },
    price: { type: Number, required: false, default: 179900 },
    recurrence: { type: String, required: false, default: 'Yearly' },
    isServiceLoading: { type: Boolean, default: false },
  },

  data() {
    return {
      form: form({ model, rules }),
      dialog: {
        address: dialog(),
      },
      list: {
        files: [],
        optionsIdentifications: [],
      },
      loading: {
        personAddress: false,
      },
      model: {
        rut: '',
        response: {},
        registerSuccess: false,
        userExists: {},
      },

      // Configuración de precios para usuarios adicionales
      pricing: {
        unitPrice: 25000, // Precio por usuario adicional en COP
      },
    }
  },

  computed: {
    // Computed property para controlar el skeleton loader
    isFormLoading() {
      return this.isServiceLoading
    },

    isAliaddo: {
      get(_this) {
        return _this.form.model.ipAddress === '186.31.27.24'
      },
    },

    labelIdentificationNumber: {
      get(_this) {
        if (_this.form.model.company.identificationTypeCode === 'CC') {
          return i18n.tc('identificationCc')
        }
        if (_this.form.model.company.identificationTypeCode === 'NIT') {
          return i18n.tc('identificationNit')
        }
        if (_this.form.model.company.identificationTypeCode === 'TE') {
          return i18n.tc('identificationTe')
        }
        if (_this.form.model.company.identificationTypeCode === 'PS') {
          return i18n.tc('identificationPs')
        }

        return i18n.tc('identificationYourCompany')
      },
    },

    // Cálculo del costo adicional por usuarios
    additionalCost() {
      return (this.form.model.additionalUsers || 0) * this.pricing.unitPrice
    },
  },

  mounted() {
    this.getIp()
    this.fillOptions()

    // Inicializar usuarios adicionales si no existe
    if (!this.form.model.additionalUsers) {
      this.$set(this.form.model, 'additionalUsers', 0)
    }
  },

  methods: {
    fillCompanyName() {
      const _this = this
      if (_this.form.model.company.identificationTypeCode === 'CC') {
        _this.form.model.company.companyName = _this.form.model.userFullName
      }
    },

    showDialog,

    // Manejar cambio en usuarios adicionales
    onAdditionalUsersChange({ addOn, value }) {
      addOn.billedValue = roundPrecision(value * addOn.price, 0)

      // Actualizar el array de add-ons
      const index = this.addOnsMandatory.findIndex(item => item.id === addOn.id)
      if (index !== -1) {
        this.$set(this.addOnsMandatory, index, { ...addOn, quantity: value })
      }
    },

    onChangeEmail(event) {
      const _this = this
      if (event && +event.length > 0) {
        _this.form.loading = true
        _this.$http
          .get(`users/${event}/findByEmail`)
          .then(result => {
            _this.model.userExists = result.data
            if (result.data) {
              _this.form.model = {
                ..._this.form.model,
                ...{
                  userName: result.data.userName,
                  email: event,
                },
              }
            } else {
              _this.form.model.email = event
            }
          })
          .catch(error => {
            console.error('Error al validar email:', error)
          })
          .finally(() => {
            _this.form.loading = false
          })
      } else {
        _this.form.model = {
          ..._this.form.model,
          ...{
            userName: '',
            phone: '',
            email: '',
          },
        }
        _this.model.userExists = {}
      }
    },

    onChangeIdentification(value) {
      const _this = this
      _this.form.model.company.companyIdentification = value
      _this.form.model.company.companyIdentificationCheck = calculateIdentificationCheck(value)
    },

    onGoToService() {
      const _this = this
      if (process.env.NODE_ENV === 'production') {
        window.location.replace(`https://${this.service.serviceDomain}`, '_blank')
      } else if ([2, 9, 11, 13].includes(_this.service.id)) {
        window.location.replace('https://aliaddo-test-erp.firebaseapp.com', '_blank')
      } else if ([10].includes(_this.service.id)) {
        window.location.replace('https://aliaddo-test-hrm.firebaseapp.com', '_blank')
      } else if ([7, 12].includes(_this.service.id)) {
        window.location.replace('https://aliaddo-test-isv.firebaseapp.com', '_blank')
      }
    },

    async onRegister(formData) {
      const _this = this
      _this.form.errors = []

      try {
        // Validar formulario
        const isValid = await new Promise(resolve => {
          // Aquí puedes agregar validaciones personalizadas adicionales
          resolve(true)
        })

        if (!isValid) {
          validateForm()

          return
        }

        // Procesar nombre completo
        const userFullNameParts = formData.fullName.trim().split(' ')
        let firstName; let
          lastName

        if (userFullNameParts.length >= 4) {
          firstName = `${userFullNameParts[0].trim()} ${userFullNameParts[1].trim()}`
          lastName = `${userFullNameParts[2].trim()} ${userFullNameParts[3].trim()}`
        } else if (userFullNameParts.length === 3) {
          firstName = `${userFullNameParts[0].trim()}`
          lastName = `${userFullNameParts[1].trim()} ${userFullNameParts[2].trim()}`
        } else if (userFullNameParts.length === 2) {
          firstName = `${userFullNameParts[0].trim()}`
          lastName = `${userFullNameParts[1].trim()}`
        } else if (userFullNameParts.length === 1) {
          firstName = formData.fullName.trim()
          lastName = formData.fullName.trim()
        }

        // Preparar datos para envío
        const submitData = {
          ...formData,
          firstName,
          lastName,
          userName: formData.email
            .replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/]/gi, '')
            .trim()
            .slice(0, 23),
          userEmail: formData.email.trim(),
          company: {
            ...this.company,
            ...formData.company,
            serviceId: this.service.id,
            planId: this.plan.id,
            companyServicePrice: this.price,
            companyServiceFrequencyBilling: this.recurrence,
            couponId: this.coupon.id,
            couponDiscount: this.coupon.discount,
            couponDiscountType: this.coupon.discountType,
            couponRedemptionLimit: this.coupon.redemptionLimit,
            companyName: formData.company.companyName.trim(),
            companyIdentification: formData.company.companyIdentification.trim(),

            // additionalUsers: this.form.model.additionalUsers || 0,
            // additionalUsersCost: this.additionalCost
          },
          seller: this.$route.query.seller,
          source: this.$route.query.source,
          ipAddress: this.form.model.ipAddress,
        }

        // Determinar source
        if (this.$route.query.source === 'crm' || this.$route.query.seller) {
          submitData.company.companyServiceSource = 'Crm'
        } else {
          submitData.company.companyServiceSource = 'Site'
        }

        const formDataToSend = new FormData()
        formDataToSend.append('signup', JSON.stringify(submitData))

        _this.form.loading = true

        // Analytics tracking
        const totalValue = _this.serviceAmountTotal + _this.additionalCost

        // Google Analytics tracking

        if (_this.$gtag) {
          _this.$gtag.event('lead', {
            ecommerce: {
              purchase: {
                actionField: {
                  id: submitData.company.companyIdentification,
                  affiliation: _this.plan.planName,
                  revenue: totalValue,
                  tax: _this.serviceTaxValue,
                  shipping: _this.serviceAmount,
                  coupon: _this.coupon.code || '',
                },
                products: [
                  {
                    name: _this.plan.planName,
                    id: _this.plan.id,
                    price: _this.serviceAmount,
                    brand: 'Aliaddo',
                    category: 'Service',
                    quantity: 1,
                    coupon: _this.coupon.code || '',
                  },
                  ...((_this.form.model.additionalUsers > 0) ? [{
                    name: `Usuarios Adicionales (${_this.form.model.additionalUsers})`,
                    id: 'additional-users',
                    price: _this.additionalCost,
                    brand: 'Aliaddo',
                    category: 'Add-on',
                    quantity: _this.form.model.additionalUsers,
                    coupon: '',
                  }] : []),
                ],
              },
            },
          })

          _this.$gtag.event('lead', {
            affiliation: _this.plan.planName,
            value: totalValue,
            planPricing: totalValue,
            currency: 'COP',
            additionalUsers: _this.form.model.additionalUsers,
            additionalUsersCost: _this.additionalCost,
          })
        }

        // Enviar datos
        await _this.$http.post('companies', formDataToSend)
        _this.model.registerSuccess = true

        // Mostrar mensaje de éxito
        _this.$message.success('¡Registro exitoso! Revisa tu correo electrónico.')
      } catch (error) {
        console.error('Error en registro:', error)
        _this.$message.error('Error al procesar el registro. Inténtalo nuevamente.')
      } finally {
        setTimeout(() => {
          _this.form.loading = false
        }, 1000)
      }
    },

    fillOptions() {
      const _this = this
      _this.$http.get('identificationTypes').then(response => {
        if (response.body && response.body.length > 0) {
          _this.list.optionsIdentifications = response.body.filter(item => ['31', '13'].includes(item.id))
        } else {
          _this.list.optionsIdentifications = []
        }
      }).catch(error => {
        console.error('Error al cargar tipos de identificación:', error)
        _this.list.optionsIdentifications = []
      })
    },

    getIp() {
      axios.get('https://api.ipify.org?format=json').then(response => {
        this.form.model.ipAddress = response.data.ip
      }).catch(() => {
        this.form.model.ipAddress = '127.0.0.1'
      })
    },

    // Obtener emoji aleatorio con colores
    getRandomEmoji() {
      const symbols = ['★', '✦', '✧', '◆', '◇', '●', '◯', '▲', '▼', '♦', '♥', '♠', '♣', '✨', '⭐', '◈']

      return symbols[Math.floor(Math.random() * symbols.length)]
    },

    // Generar estilos para confetti que explota desde el centro del checkmark
    getConfettiStyle(index) {
      const colors = ['#4F46E5', '#7C3AED', '#06D6A0', '#FFD60A', '#FF6B6B', '#4ECDC4', '#F093FB', '#F5576C']

      // Crear explosión radial desde el centro
      const angle = (360 / 50) * index + Math.random() * 15
      const distance = 120 + Math.random() * 180 // Distancia más controlada
      const duration = 1.5 + Math.random() * 1
      const delay = Math.random() * 0.2
      const size = 0.7 + Math.random() * 0.5
      const color = colors[Math.floor(Math.random() * colors.length)]

      // Convertir ángulo a radianes para calcular posición final
      const radian = (angle * Math.PI) / 180
      const finalX = Math.cos(radian) * distance
      const finalY = Math.sin(radian) * distance

      return {
        left: '50%',
        top: '35%', // Ajustado para nueva posición del checkmark
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
        fontSize: `${size}rem`,
        color,
        '--final-x': `${finalX}px`,
        '--final-y': `${finalY}px`,
        '--rotation': `${Math.random() * 360}deg`,
      }
    },

  },
}
</script>

<style>
.flex-container {
  display: flex;
  flex-direction: row;
  text-align: center;
}

.flex-item {
  padding: 2px;
  flex: 33%;
}

.el-form-item--mini.el-form-item,
.el-form-item--small.el-form-item {
  margin-bottom: 10px;
}

.el-form-item__label {
  line-height: 30px;
}
</style>

<style lang="scss" scoped>
@import '@/assets/scss/transactions/register.scss';
@import '@/assets/scss/transactions/personalize.scss';

// Estilos adicionales para compatibilidad
.signup-integration {
  width: 100%;
  min-height: 100vh;
}

/* Mantener compatibilidad con estilos existentes */
.users-section {
  background: #f8f9ff;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e1e8ff;
  margin-top: 10px;
}

.users-info {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  gap: 8px;
}

.info-icon {
  color: #409eff;
  font-size: 16px;
}

.info-text {
  font-size: 14px;
  color: #666;
}

.users-selector {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
}

.users-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.pricing-details {
  background: white;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e1e8ff;
}

.pricing-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.pricing-row:last-child {
  margin-bottom: 0;
}

.pricing-label {
  font-size: 14px;
  color: #666;
  flex: 1;
}

.pricing-value {
  font-weight: 600;
  font-size: 14px;
}

.unit-price {
  color: #909399;
}

.pricing-breakdown {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.additional-price {
  color: #409eff;
}

.pricing-divider {
  height: 1px;
  background: #e4e7ed;
  margin: 12px 0;
}

.total {
  background: #f8f9ff;
  padding: 8px 12px;
  border-radius: 6px;
  margin: 8px -12px -12px -12px;
}

.total-label {
  font-weight: 600;
  color: #303133;
}

.total-price {
  color: #67c23a;
  font-size: 16px;
  font-weight: 700;
}

/* Responsive */
@media (max-width: 768px) {
  .users-selector {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .users-selector .el-input-number {
    width: 100% !important;
  }

  .pricing-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}

.success-page {
  min-height: 100vh;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100vh;
  margin: 0;
  padding: 0;
}

.confetti-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.confetti {
  position: absolute;
  width: 10px;
  height: 10px;
  background: #ff6b6b;
  animation: confetti-fall linear infinite;
  transform-origin: center;
}

.confetti-emoji {
  width: auto;
  height: auto;
  background: transparent;
  font-size: 1rem;
  line-height: 1;
  animation: confetti-emoji-fall linear infinite;
}

.confetti-emoji::before {
  content: attr(data-emoji);
  display: block;
  animation: confetti-emoji-spin linear infinite;
}

@keyframes confetti-emoji-fall {
  0% {
    transform: translateY(-100vh) rotate(0deg) scale(1);
    opacity: 1;
  }
  25% {
    opacity: 1;
  }
  100% {
    transform: translateY(100vh) rotate(360deg) scale(0.3);
    opacity: 0;
  }
}

@keyframes confetti-emoji-spin {
  0% {
    transform: rotateY(0deg);
  }
  50% {
    transform: rotateY(180deg);
  }
  100% {
    transform: rotateY(360deg);
  }
}

.success-content {
  background: white;
  border-radius: 15px;
  padding: 30px 25px;
  text-align: center;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  max-width: 380px;
  width: 90%;
  position: relative;
  z-index: 2;
  overflow: hidden;
}

.success-icon {
  margin-bottom: 15px;
}

.checkmark-circle {
  width: 55px;
  height: 55px;
  margin: 0 auto;
  position: relative;
}

.checkmark {
  width: 55px;
  height: 55px;
  border-radius: 50%;
  display: block;
  stroke-width: 3;
  stroke-miterlimit: 10;
  animation: checkmark-fill 0.4s ease-in-out 0.4s forwards, checkmark-scale 0.3s ease-in-out 0.9s both;
}

.checkmark-circle-bg {
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  stroke-width: 3;
  stroke-miterlimit: 10;
  fill: none;
  animation: checkmark-stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
}

.checkmark-check {
  transform-origin: 50% 50%;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  animation: checkmark-stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards;
}

@keyframes checkmark-stroke {
  100% {
    stroke-dashoffset: 0;
  }
}

@keyframes checkmark-scale {
  0%, 100% {
    transform: none;
  }
  50% {
    transform: scale3d(1.1, 1.1, 1);
  }
}

@keyframes checkmark-fill {
  100% {
    box-shadow: inset 0px 0px 0px 30px transparent;
  }
}

.success-message {
  margin-bottom: 20px;
}

.success-title {
  font-size: 22px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 10px 0;
  line-height: 1.3;
}

.success-description {
  font-size: 13px;
  color: #7f8c8d;
  line-height: 1.4;
  margin: 0;
}

.success-actions {
  margin-bottom: 0;
}

.success-button {
  background: linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%);
  border: none;
  border-radius: 25px;
  padding: 12px 28px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  transition: all 0.3s ease;
  box-shadow: 0 6px 20px rgba(79, 70, 229, 0.3);

  &:hover {
    background: linear-gradient(135deg, #4338CA 0%, #6D28D9 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(79, 70, 229, 0.4);
  }
}

@media (max-width: 768px) {
  .success-page {
    padding: 15px;
  }

  .success-content {
    padding: 25px 20px;
    max-width: 320px;
  }

  .success-title {
    font-size: 18px;
  }

  .success-description {
    font-size: 12px;
  }

  .checkmark-circle {
    width: 45px;
    height: 45px;
  }

  .checkmark {
    width: 45px;
    height: 45px;
  }

  .success-button {
    padding: 10px 24px;
    font-size: 13px;
  }
}
body {
  overflow: hidden !important;
}
html, body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
}
</style>
